import { type User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { useSyncExternalStore } from "react";

// The session of the back-office (decision 46): a person signs in through Keycloak (OpenID Connect, code with PKCE, client
// seamap-front) and the application sends the access token with each request of the back-office. The settings come from the service
// (GET /v1/auth/config), so that one build serves every stack. The tokens live in the memory of the tab (sessionStorage) and are renewed
// before they expire with the refresh token; « Se déconnecter » ends the session in Keycloak too.

export type SessionStatus = "loading" | "signed-out" | "signed-in" | "unavailable";

interface State {
  status: SessionStatus;
  user: User | null;
}

const CALLBACK = "/connexion";
let state: State = { status: "loading", user: null };
const listeners = new Set<() => void>();
let manager: Promise<UserManager | null> | null = null;

function set(next: State): void {
  state = next;
  for (const l of listeners) l();
}

function fromUser(user: User | null): State {
  return user && !user.expired ? { status: "signed-in", user } : { status: "signed-out", user: null };
}

async function create(): Promise<UserManager | null> {
  const r = await fetch(`${window.location.origin}/v1/auth/config`);
  if (!r.ok) throw new Error(`réglages de connexion : ${r.status}`);
  const cfg = (await r.json()) as { issuer: string | null; client_id: string };
  if (!cfg.issuer) return null;
  const m = new UserManager({
    authority: cfg.issuer,
    client_id: cfg.client_id,
    redirect_uri: `${window.location.origin}${CALLBACK}`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    response_type: "code",
    scope: "openid profile email",
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
    monitorSession: false,
    loadUserInfo: false,
  });
  m.events.addUserLoaded((u) => set(fromUser(u)));
  m.events.addUserUnloaded(() => set({ status: "signed-out", user: null }));
  m.events.addAccessTokenExpired(() => set({ status: "signed-out", user: null }));
  m.events.addSilentRenewError(() => set({ status: "signed-out", user: null }));
  return m;
}

/** The manager of the session, made once from the settings of the service; null when the service has no Keycloak. */
export function userManager(): Promise<UserManager | null> {
  if (!manager) {
    manager = create().then(
      async (m) => {
        set(m ? fromUser(await m.getUser()) : { status: "unavailable", user: null });
        return m;
      },
      (e: unknown) => {
        set({ status: "unavailable", user: null });
        manager = null;
        throw e;
      },
    );
  }
  return manager;
}

/** The access token of the session, for the requests of the back-office (none: not signed in). */
export function accessToken(): string | null {
  return state.user && !state.user.expired ? state.user.access_token : null;
}

/** Goes to Keycloak to sign in, then comes back to `returnTo`. */
export async function signIn(returnTo: string = window.location.pathname + window.location.search): Promise<void> {
  const m = await userManager();
  if (m) await m.signinRedirect({ state: { returnTo } });
}

/** Ends the session here and in Keycloak, then comes back to the map. */
export async function signOut(): Promise<void> {
  const m = await userManager();
  if (!m) return;
  const user = await m.getUser();
  await m.removeUser();
  await m.signoutRedirect({ id_token_hint: user?.id_token });
}

let callback: Promise<string> | null = null;

/** Ends the sign-in at the return from Keycloak (once, even when React mounts twice); returns the address to go back to. */
export function completeSignIn(): Promise<string> {
  callback ??= userManager().then(async (m) => {
    if (!m) return "/";
    const user = await m.signinRedirectCallback();
    set(fromUser(user));
    const returnTo = (user.state as { returnTo?: string } | undefined)?.returnTo;
    return returnTo?.startsWith("/") && !returnTo.startsWith(CALLBACK) ? returnTo : "/";
  });
  return callback;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  void userManager().catch(() => undefined);
  return () => listeners.delete(listener);
}

/** The state of the session, for the components. */
export function useSession(): State {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}
